import { useState, useEffect } from 'react';
import { ArrowLeft, Users, RefreshCw, Clock, Search } from 'lucide-react';
import api from '../../utils/api';

const ActiveTodayTab = ({ onBack }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchActiveToday = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/api/attendance/active-today', {});
      setEntries(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching active today:', err);
      setError('Failed to fetch active today list.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredEntries = entries.filter(e => {
    if (!searchQuery) return true;
    return e.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getAvatarInitials = (name) => {
    if (!name) return 'JD';
    const parts = name.split(' ');
    return `${parts[0]?.[0] || ''}${parts[1]?.[0] || ''}`.toUpperCase();
  };

  const formatCheckinTime = (timeStr) => {
    if (!timeStr) return '—';
    const date = new Date(timeStr);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users size={24} className="text-lime-400" />
              Active Today
            </h1>
            <p className="text-gray-400 text-sm">Members checked in today</p>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400 text-sm"
            />
          </div>
          <button
            onClick={fetchActiveToday}
            disabled={loading}
            className="p-2.5 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading today's activity...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-6">{error}</div>
      ) : (
        <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[500px]">
              <thead>
                <tr className="bg-[#222] border-b border-[#2b2b2b] text-gray-300 font-semibold text-xs uppercase tracking-wider">
                  <th className="px-6 py-4 w-16">Photo</th>
                  <th className="px-6 py-4">Name</th>
                  <th className="px-6 py-4">Username/Phone</th>
                  <th className="px-6 py-4">First Check-in Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2b2b2b] text-sm text-gray-200">
                {filteredEntries.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center text-gray-500 bg-[#1a1a1a]">
                      No check-ins logged for today yet.
                    </td>
                  </tr>
                ) : (
                  filteredEntries.map((entry, idx) => (
                    <tr key={idx} className="hover:bg-[#222]/50 transition-colors">
                      <td className="px-6 py-3">
                        <div className="w-10 h-10 rounded-full bg-lime-400/10 flex items-center justify-center text-lime-400 relative flex-shrink-0">
                          {entry.photoUrl ? (
                            <img
                              src={entry.photoUrl}
                              alt={entry.name}
                              className="w-full h-full object-cover rounded-full"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="text-sm font-bold">
                              {getAvatarInitials(entry.name)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium text-white">{entry.name}</td>
                      <td className="px-6 py-4 text-gray-400">
                        {entry.username?.startsWith('@') ? entry.username.slice(1) : entry.username}
                      </td>
                      <td className="px-6 py-4">
                        <span className="flex items-center gap-1.5 text-lime-400 font-medium">
                          <Clock size={14} />
                          {formatCheckinTime(entry.checkInTime)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveTodayTab;
